import template from 'lodash/template';
import reduce from 'lodash/reduce';
import range from 'lodash/range';
import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';
import Mustache from 'mustache';

export {camelCase, snakeCase};

/**
 * Concise shorthand for Mustache.render
 * IMT = Interpolate Mustache Template
 */
export const IMT = Mustache.render;

export type TemplateRenderFunction = (
  template: string,
  render: (t: string) => string
) => string;

/**
 * A web-specific text-bolding handler for mustache templates
 */
export const strongify =
  (): TemplateRenderFunction =>
  (template: string, render: (t: string) => string) =>
    `<strong>${render(template)}</strong>`;

/**
 * Uppercase the first character.
 */
export const ucFirst = (str: string): string =>
  str.substr(0, 1).toUpperCase() + str.substr(1);

/**
 * Stolen from https://locutus.io/php/strings/ucwords/
 *
 * @param str
 */
export const ucWords = (str: string): string =>
  str.replace(/^(.)|\s+(.)/g, $1 => $1.toUpperCase());

export const nl2br = (str: string): string => str.replace(/\n/g, '<br/>');
export const br2nl = (str: string): string => str.replace(/<br[\s/]*>/g, '\n');

export function replaceHyperlinks(
  str: string,
  newSubStr = '${content} (${href})'
): string {
  return str.replace(
    /<a\s[^>]*href="([^"]*)"[^>]*>([^<]+)<\/a>/gi,
    (link, href, content) => template(newSubStr)({href, content})
  );
}

export function stripHTMLTags(str: string, passes = 2): string {
  return range(passes).reduce(carry => {
    const container = document.implementation.createHTMLDocument('New').body;
    container.innerHTML = carry;
    return container.textContent || container.innerText || '';
  }, str);
}

export function stripHTMLTag(str: string, tag: string): string {
  return str.replace(new RegExp(`(<\/?[${tag}][^>]+>)`, 'ig'), '');
}

/**
 * Translates htmlspecialchars into their appropriate entities.
 */
export function replaceSpecialCharactersWithHtml(str: string): string {
  const map: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#039;': "'",
  };

  return str.replace(
    new RegExp(Object.keys(map).join('|'), 'g'),
    symbol => map[symbol]
  );
}

/**
 * Translate an object to HTML attributes.
 */
export function objectToHtmlAttributes(object: Record<string, string>): string {
  return reduce(
    object,
    (result: string, value: string, attribute: string): string => {
      return `${result} ${attribute}="${value}"`.trim();
    },
    ''
  );
}

/**
 * Just a sugar function for helping with matching.
 * It is meant to be extended to specific use-cases like location matching.
 */
export function extractFrom(string: string, regex: RegExp): string | null {
  const match = string.match(regex);
  return match && match[1];
}

/**
 * @see https://stackoverflow.com/a/53472013/11882366
 */
const urlRegex = /[^"](https?:\/\/\S*)/g;

export function convertUrlsToLinks(string: string): string {
  function makeAnchor(url: string) {
    const anchor = document.createElement('a');
    anchor.setAttribute('href', url);
    anchor.textContent = url;
    return anchor;
  }

  return string.replace(urlRegex, url => {
    const anchor = makeAnchor(url.trim());

    /**
     * This is temporary. Negative lookbehind, which is what we were using previously to exclude urls that were part of an HTML attribute, don't work in Safari.
     * To get around this, we just don't match if the link is preceded by ". However, that also matches spaces. Check to see if the matched url is preceded with a space,
     * and if so make sure to preserve that space in the output.
     *
     * I imagine we will rethink the usage of this and have the backend send down urls already formatted with anchors.
     */
    if (url.startsWith(' ')) {
      // eslint-disable-next-line
      return ' ' + anchor.outerHTML;
    }

    return anchor.outerHTML;
  });
}
